
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  InputOTP,
  InputOTPGroup,
  InputOTPSlot 
} from "@/components/ui/input-otp";
import { cleanupRecaptcha } from "@/lib/phoneAuth";

interface PhoneVerificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string;
  onVerify: (code: string) => Promise<void>;
  error: string | null;
}

export function PhoneVerificationDialog({
  isOpen,
  onClose,
  phoneNumber,
  onVerify,
  error
}: PhoneVerificationDialogProps) {
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  
  // Reset timer when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTimeLeft(60);
      setVerificationCode("");
    }
  }, [isOpen]);
  
  // Handle countdown timer
  useEffect(() => {
    let timerId: number | undefined;
    
    if (isOpen && timeLeft > 0) {
      timerId = window.setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    }
    
    return () => {
      if (timerId !== undefined) {
        clearInterval(timerId);
      }
    };
  }, [isOpen, timeLeft]);
  
  const handleVerify = async () => {
    if (verificationCode.length !== 6) return;
    
    try {
      setIsVerifying(true);
      await onVerify(verificationCode);
    } finally {
      setIsVerifying(false);
    }
  };
  
  const formatPhoneNumber = (phone: string) => {
    // Display only the last 4 digits for privacy
    return phone.replace(/\d(?=\d{4})/g, "*");
  };
  
  const handleResend = () => {
    cleanupRecaptcha(); // Clean up resources before closing
    onClose();
    // User will need to trigger the verification process again
  };
  
  const handleDialogClose = () => {
    cleanupRecaptcha(); // Ensure cleanup when dialog is closed
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleDialogClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Verify Your Phone Number</DialogTitle>
          <DialogDescription>
            Enter the 6-digit verification code sent to {formatPhoneNumber(phoneNumber)}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col items-center py-4">
          <InputOTP
            maxLength={6}
            value={verificationCode}
            onChange={setVerificationCode}
            className="mb-4"
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
          
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <p className="text-sm text-muted-foreground mb-4">
            {timeLeft > 0 ? (
              <>Code expires in {timeLeft} seconds</>
            ) : (
              <>Code expired. Please request a new code.</>
            )}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <Button 
              variant="secondary" 
              onClick={handleResend}
              disabled={timeLeft > 0}
            >
              Resend Code
            </Button>
            <Button 
              onClick={handleVerify} 
              disabled={verificationCode.length !== 6 || isVerifying}
            >
              {isVerifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying
                </>
              ) : (
                "Verify"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
